import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Collapse, Nav, Badge } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import { useTranslation } from 'react-i18next';
import { SidenavItemType } from './SidenavItemTypes';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { checkPermission } from '../../../features/auth/authSlice';
import {
  getPendingFeedbacksCountAsync,
  selectPendingFeedbacksCount,
} from '../../../features/feedbacks/FeedbacksSlice';

interface SidenavItemProps {
  ActivePath: string;
  HandleCloseSubmenu: () => void;
  Item: SidenavItemType;
}

const SidenavItem = ({
  ActivePath,
  HandleCloseSubmenu,
  Item,
}: SidenavItemProps) => {
  const { t } = useTranslation(['sidenav']);
  const dispatch = useAppDispatch();
  const [isSubmenuOpen, setIsSubmenuOpen] = useState<boolean>(false);
  const [activePathItems, setActivePathItems] = useState<string[]>([]);
  const [isActiveItem, setIsActiveItem] = useState<boolean>(false);
  const pendingFeedbacksCount = useAppSelector(selectPendingFeedbacksCount);

  useEffect(() => {
    const tempItems: string[] = [];

    if (Item.Url) {
      tempItems.push(Item.Url);
    }

    if (Item.ActivePaths && Item.ActivePaths.length > 0) {
      Item.ActivePaths.forEach(function (path) {
        tempItems.push(path);
      });
    }

    if (Item.SubItems && Item.SubItems.length > 0) {
      Item.SubItems.forEach(function (subitem) {
        tempItems.push(subitem.Url);
      });
    }

    setActivePathItems(tempItems);
  }, [Item]);

  useEffect(() => {
    let tempActiveStatus = false;

    if (activePathItems.length > 0) {
      activePathItems.forEach(function (pathItem) {
        if (ActivePath.startsWith(pathItem)) {
          tempActiveStatus = true;
        }
      });
    }

    setIsActiveItem(tempActiveStatus);
    setIsSubmenuOpen(tempActiveStatus);
  }, [ActivePath, activePathItems]);

  useEffect(() => {
    if (Item.Name === 'Palautteet') {
      dispatch(getPendingFeedbacksCountAsync());
    }
  }, [Item.Name, dispatch]);

  const handleLinkClick = () => {
    HandleCloseSubmenu();
  };

  const hasPermission = useAppSelector(checkPermission(Item.Permissions));

  // Do not return anything if user doesn't have permission to show current side navigation item
  if (!hasPermission) {
    return null;
  }

  const getTranslatedName = (item: {
    Name: string;
    TranslationKey?: string;
  }) => {
    if (item.TranslationKey) {
      return t(`sidenav:items.${item.TranslationKey}`, item.Name);
    }
    return item.Name;
  };

  if (Item.SubItems && Item.SubItems.length > 0) {
    return (
      <>
        <Nav.Item>
          <Nav.Link
            className={`sidenav-link${
              isActiveItem ? ' text-body ' : ' '
            }d-flex align-items-center justify-content-stretch px-24 ps-16 py-8 py-xl-12`}
            onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
          >
            <Icon
              Name={Item.Icon}
              CssClass={isActiveItem ? 'text-primary' : ''}
            />
            <span
              className={`${isActiveItem ? 'fw-medium' : ''} body-small d-block flex-grow-1 mx-16 text-nowrap`}
            >
              {getTranslatedName(Item)}
            </span>
            <Icon Name={isSubmenuOpen ? 'chevron-up' : 'chevron-down'} />
          </Nav.Link>
        </Nav.Item>
        <Collapse in={isSubmenuOpen}>
          <div className="nav nav-sm flex-column flex-nowrap mb-8">
            {Item.SubItems.map(item => (
              <Nav.Item
                key={encodeURI(item.Name)}
                className="d-block flex-grow-1"
              >
                <Nav.Link
                  as={Link}
                  to={item.Url}
                  className={`sidenav-subitem-link${
                    item.Url === ActivePath
                      ? ' sidenav-subitem-link-active text-body '
                      : ' '
                  }py-4 ps-48 position-relative`}
                  onClick={() => handleLinkClick()}
                >
                  <span
                    className={`${item.Url === ActivePath ? 'fw-medium' : ''} ms-8 d-block body-small`}
                  >
                    {getTranslatedName(item)}
                  </span>
                </Nav.Link>
              </Nav.Item>
            ))}
          </div>
        </Collapse>
      </>
    );
  }

  return (
    <Nav.Item>
      <Nav.Link
        as={Link}
        to={Item.Url}
        className={`sidenav-link${
          Item.Url === ActivePath || isActiveItem ? ' text-body ' : ' '
        }d-flex align-items-center justify-content-stretch px-24 ps-16 py-12`}
        onClick={() => handleLinkClick()}
      >
        <Icon CssClass={isActiveItem ? 'text-primary' : ''} Name={Item.Icon} />
        <span className="body-small ms-16">{Item.Name}</span>
        {Item.Name === 'Palautteet' && pendingFeedbacksCount > 0 && (
          <Badge bg="danger" className="ms-auto">
            {pendingFeedbacksCount}
          </Badge>
        )}
      </Nav.Link>
    </Nav.Item>
  );
};

export default SidenavItem;
export { SidenavItem };
export type { SidenavItemProps };
